import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import z from 'zod';
import { BidDeliverables } from '@/features/bid-deliverables/components/bid-deliverables';
import { BidDeliverablesRefreshAlert } from '@/features/bid-deliverables/components/bid-deliverables-refresh-alert';
import { bidDeliverablesQuery } from '@/features/bid-deliverables/queries';
import { BID_QUALIFICATION_CRITERION_ID } from '@/features/bid-deliverables/constants';
import { pendingDeliverableFilesAtom } from '@/features/bid-deliverables/store';
import { isInCriterion } from '@/features/bid-deliverables/utils';
import { bidComplianceQuery } from '@/features/bid-compliance/queries';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/ui/components/tabs';
import { H1 } from '@/ui/components/heading';
import {
  MainHeader,
  MainHeaderBackButton,
  MainNav,
  MainNavList,
  MainScrollArea,
} from '@/ui/components/main';
import { AwardCriteriaSheet } from '@/features/bids/components/award-criteria';
import { Header, HeaderBackButton, HeaderChatButton } from '@/ui/components/header';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Badge } from '@/ui/components/badge';
import { Icon } from '@/ui/components/icon';
import { CheckmarkBadge01Icon, Files01Icon } from '@hugeicons-pro/core-stroke-rounded';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/deliverables/',
)({
  component: RouteComponent,
  validateSearch: z.object({
    section: z.enum(['qualification', 'tender']).default('qualification').catch('qualification'),
  }),
  onEnter: () => getDefaultStore().set(pendingDeliverableFilesAtom, []),
});

function RouteComponent() {
  const { bidId, workspaceId, projectId } = Route.useParams();
  const { section } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
          params={{ projectId, workspaceId, bidId }}
        />
        <BidBreadcrumbs>
          <BreadcrumbPage>{t({ en: 'Deliverables', nb: 'Leveranser' })}</BreadcrumbPage>
        </BidBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <SuspenseBoundary>
          <BidDeliverablesRefreshAlert />
        </SuspenseBoundary>
        <IndexingStatusAlerts
          resourceId={workspaceId}
          documentType="tenderPackage"
          waitForKg
          showKgRebuild={false}
        />
        <MainHeader>
          <H1>
            <MainHeaderBackButton
              to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
              params={{ projectId, workspaceId, bidId }}
            />
            {t({ en: 'Deliverables', nb: 'Leveranser' })}
          </H1>
        </MainHeader>
        <MainNav>
          <MainNavList>
            <AwardCriteriaSheet />
          </MainNavList>
        </MainNav>
        <Tabs
          className="flex flex-col grow"
          value={section}
          onValueChange={(value) =>
            navigate({ replace: true, search: (prev) => ({ ...prev, section: value }) })
          }
        >
          <TabsList className="px-x">
            <TabsTab variant="folder" value="qualification">
              <Icon icon={CheckmarkBadge01Icon} />
              {t(common.qualification)}
              <SuspenseBoundary>
                <QualificationCount />
              </SuspenseBoundary>
            </TabsTab>
            <TabsTab variant="folder" value="tender">
              <Icon icon={Files01Icon} />
              {t({ en: 'Tender', nb: 'Tilbud' })}
              <SuspenseBoundary>
                <TenderCount />
              </SuspenseBoundary>
            </TabsTab>
          </TabsList>
          <TabsPanel value="qualification" className="relative min-h-0 grow">
            <BidDeliverables section="qualification" />
          </TabsPanel>
          <TabsPanel value="tender" className="relative min-h-0 grow">
            <BidDeliverables section="tender" />
          </TabsPanel>
        </Tabs>
      </MainScrollArea>
    </>
  );
}

function QualificationCount() {
  const { workspaceId, bidId } = Route.useParams();
  const { data: compliance } = useSuspenseQuery(bidComplianceQuery(workspaceId, bidId));
  const { data: deliverables } = useSuspenseQuery(
    bidDeliverablesQuery(workspaceId, bidId, BID_QUALIFICATION_CRITERION_ID),
  );
  if (!deliverables.length && compliance.deliverableCatalog?.state === 'ready')
    return (
      <Badge size="xxs" variant="tertiary">
        {t({ en: 'Not found', nb: 'Ikke funnet' })}
      </Badge>
    );
  const count = deliverables.filter((deliverable) => !deliverable.dismissed).length;
  if (!count) return null;
  return (
    <Badge size="xxs" className="tabular-nums">
      {count}
    </Badge>
  );
}

function TenderCount() {
  const { workspaceId, bidId } = Route.useParams();
  const { data: deliverables } = useSuspenseQuery(bidDeliverablesQuery(workspaceId, bidId));
  const count = deliverables.filter(
    (deliverable) =>
      !deliverable.dismissed && !isInCriterion(deliverable, BID_QUALIFICATION_CRITERION_ID),
  ).length;
  if (!count) return null;
  return (
    <Badge size="xxs" className="tabular-nums">
      {count}
    </Badge>
  );
}
